import { ui } from "@/components/ui";
import { SideZ } from "@/types/zod/common";
import { type SubmitHandler, useForm, z, zodResolver } from "@/utils/form";
import { useSurgeryMutation } from "../api/upsertSurgery";
import { SurgeryFormFields } from "./SurgeryFormFields";

interface Props {
	diaryId: string;
	eventId: string;
	surgeryId: string;
	onSuccess?: () => void;
	surgery?: {
		surgery_option_id?: string;
		orthopaedic_area?: string;
		side?: typeof SideZ._type;
	};
}

export const SurgerySchemaZ = z.object({
	event_id: z.string(),
	surgery_id: z.string(),
	side: SideZ,
	surgery_option_id: z.string().uuid(),
	orthopaedic_area: z.string().optional(),
});

export type SurgerySchemaT = z.infer<typeof SurgerySchemaZ>;

const SurgeryForm = ({
	surgery,
	diaryId,
	eventId,
	surgeryId,
	onSuccess,
}: Props) => {
	const surgeryMutation = useSurgeryMutation({ diaryId });

	const form = useForm<SurgerySchemaT>({
		resolver: zodResolver(SurgerySchemaZ),
		defaultValues: {
			event_id: eventId,
			surgery_id: surgeryId,
			surgery_option_id: surgery?.surgery_option_id ?? "",
			orthopaedic_area: surgery?.orthopaedic_area,
			side: surgery?.side,
		},
	});

	const onSubmit: SubmitHandler<SurgerySchemaT> = async (formData) => {
		surgeryMutation.mutate(
			{
				event_id: formData?.event_id,
				surgery_option_id: formData?.surgery_option_id,
				surgery_id: formData?.surgery_id,
				side: formData?.side === "" ? null : formData?.side,
			},
			{
				onSuccess: () => {
					!!onSuccess && onSuccess();
				},
			},
		);
	};

	return (
		<ui.Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="form">
				<SurgeryFormFields
					sideFieldName="side"
					orthopaedicAreaFieldName="orthopaedic_area"
					surgeryOptionIdFieldName="surgery_option_id"
				/>
				<ui.Button size="lg" type="submit">
					Next
				</ui.Button>
			</form>
		</ui.Form>
	);
};

export default SurgeryForm;
